import {
  isModifierCompletelyActiveAcrossSelection,
  isModifierCompletelyInactiveAcrossSelection,
  modifierBlocksEditActivation,
  modifierHasInvisibleActivePriceState,
  modifierPriceLockWithActivePrice,
  modifierZeroLockWithActivePrice,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';
import { Modifier } from '@/types/menus.types';
import { useMemo } from 'react';
import { TFunction } from 'i18next';

type ModifierActionButtonState = {
  isDeactivateButtonDisabled: boolean;
  shouldShowDeactivateTooltip: boolean;
  deactivateTooltipMessage: string;
  isEditPriceButtonDisabled: boolean;
  shouldShowEditPriceTooltip: boolean;
  editPriceTooltipMessage: string;
  isActivateButtonDisabled: boolean;
  shouldShowActivateTooltip: boolean;
  activateTooltipMessage: string;
};

function hasMixedZeroPricedSelection(modifiers: Modifier[]): boolean {
  if (modifiers.length < 2) return false;
  const zeroPricedCount = modifiers.filter((modifier) => modifier.isZeroPriced === true).length;
  return zeroPricedCount > 0 && zeroPricedCount < modifiers.length;
}

/**
 * Any row that is inactive in every selected store group blocks Deactivate.
 * Zero-priced locks are included, even when selected together with a priced modifier.
 */
function hasDeactivateBlockingInactiveModifier(modifiers: Modifier[], selectedStoreGroupNames: string[]): boolean {
  return modifiers.some((modifier) => isModifierCompletelyInactiveAcrossSelection(modifier, selectedStoreGroupNames));
}

function hasAllSelectedModifiersAlreadyActive(modifiers: Modifier[], selectedStoreGroupNames: string[]): boolean {
  return (
    modifiers.length > 0 &&
    modifiers.every((modifier) => isModifierCompletelyActiveAcrossSelection(modifier, selectedStoreGroupNames))
  );
}

export function useModifierActionButtonState(
  selectedModifiers: Modifier[],
  selectedRowCount: number,
  selectedStoreGroupNames: string[],
  t: TFunction,
): ModifierActionButtonState {
  return useMemo(() => {
    const hasSelection = selectedRowCount > 0;
    const mixedZeroPriced = hasMixedZeroPricedSelection(selectedModifiers);
    const priceLockedWithActivePrice = selectedModifiers.some(modifierPriceLockWithActivePrice);
    const zeroLockedWithActivePrice = selectedModifiers.some(modifierZeroLockWithActivePrice);
    const invisibleActivePriceForEdit = selectedModifiers.some(modifierHasInvisibleActivePriceState);
    const deactivateBlockingInactive = hasDeactivateBlockingInactiveModifier(
      selectedModifiers,
      selectedStoreGroupNames,
    );
    const mandatorySelected = selectedModifiers.some((modifier) => modifier.isMandatory === true);
    const alreadyActiveSelected = hasAllSelectedModifiersAlreadyActive(selectedModifiers, selectedStoreGroupNames);

    const isDeactivateButtonDisabled = !hasSelection || mandatorySelected || deactivateBlockingInactive;
    const shouldShowDeactivateTooltip = mandatorySelected || deactivateBlockingInactive;
    const deactivateTooltipMessage = mandatorySelected
      ? t('deactivateModifiers.mandatoryModifiersSelectedTooltip')
      : t('deactivateModifiers.inactiveModifiersSelectedTooltip');

    const editBlocked = mixedZeroPriced || selectedModifiers.some(modifierBlocksEditActivation);
    const isEditPriceButtonDisabled = !hasSelection || editBlocked;

    let editPriceTooltipMessage = '';
    if (mixedZeroPriced) {
      editPriceTooltipMessage = t('menu.tooltips.editModifierPriceMixedZeroPriced');
    } else if (priceLockedWithActivePrice) {
      editPriceTooltipMessage = t('menu.tooltips.editModifierPricePriceLock');
    } else if (zeroLockedWithActivePrice) {
      editPriceTooltipMessage = t('menuDetails.modifierRow.zeroPriced');
    } else if (invisibleActivePriceForEdit) {
      editPriceTooltipMessage = t('menu.tooltips.editModifierPriceInvisibleActive');
    }

    const isActivateButtonDisabled = !hasSelection || alreadyActiveSelected;
    const shouldShowActivateTooltip = hasSelection && alreadyActiveSelected;
    const activateTooltipMessage = shouldShowActivateTooltip ? t('activateModifiers.alreadyActiveTooltip') : '';

    return {
      isDeactivateButtonDisabled,
      shouldShowDeactivateTooltip,
      deactivateTooltipMessage,
      isEditPriceButtonDisabled,
      shouldShowEditPriceTooltip: hasSelection && editBlocked,
      editPriceTooltipMessage,
      isActivateButtonDisabled,
      shouldShowActivateTooltip,
      activateTooltipMessage,
    };
  }, [selectedModifiers, selectedRowCount, selectedStoreGroupNames, t]);
}
